// /frontend/src/index.js
import "leaflet/dist/leaflet.css";
import "leaflet-defaulticon-compatibility";
import "leaflet-defaulticon-compatibility/dist/leaflet-defaulticon-compatibility.css";

import React from "react";
import { createRoot } from "react-dom/client";
import { HashRouter } from "react-router-dom"; // ★ ครอบ Router ให้ useContext/useNavigate ใช้งานได้
import App from "./App.js";

const DEV = process.env.NODE_ENV !== "production";

/* =================== SERVICE WORKER =================== */
/* DEV เท่านั้น: กันแคชเพี้ยนจาก SW เก่า */
if (DEV && "serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((rs) => rs.forEach((r) => r.unregister()));
}

/* =================== DUPLICATE BUNDLE / MOUNT GUARDS =================== */
/* กันไฟล์บันเดิลถูกดึงซ้ำจากแคช/โหลดซ้อน */
if (process.env.NODE_ENV === "production") {
  if (window.__CHEETAH_APP_INITED__) {
    // eslint-disable-next-line no-console
    console.warn("[cheetah] duplicate bundle detected — skipping init");
    // ถ้าบันเดิลซ้อนจริง อย่ารันอะไรต่อ
  } else {
    window.__CHEETAH_APP_INITED__ = true;
  }
}

/* =================== EMBED PATCH (ONE-TIME, SAFE) =================== */
// รองรับ #/chat?embed=1&theme=light&bg=transparent
(function embedPatchOnce() {
  if (window.__cheetahEmbedPatched) return;
  window.__cheetahEmbedPatched = true;

  const getParams = () => {
    const hash = window.location.hash || "";
    const i = hash.indexOf("?");
    const qs = i >= 0 ? hash.slice(i + 1) : window.location.search.slice(1);
    return new URLSearchParams(qs);
  };
  const p = getParams();
  if (p.get("embed") !== "1") return;

  const de = document.documentElement;
  const body = document.body;
  if (de) de.setAttribute("data-embed", "1");
  if (de) de.classList.remove("dark-theme");
  if (body) body.classList.remove("dark-theme");
  if (de) de.style.background = "transparent";
  if (body) body.style.background = "transparent";

  if (p.get("theme") === "dark") {
    if (de) de.classList.add("dark-theme");
  }
  const bg = (p.get("bg") || "transparent").trim();
  if (body && bg) {
    const v = bg === "transparent" ? "transparent" : (bg.startsWith("#") ? bg : `#${bg}`);
    body.style.background = v;
  }
})();

/* =================== GLOBAL ERROR LOGGING =================== */
const __lastGlobal = { error: null, reason: null };
window.addEventListener("error", (e) => {
  // eslint-disable-next-line no-console
  console.error("🔥 Global JS Error:", e.message, e.error);
  __lastGlobal.error = e.error || e.message;
});
window.addEventListener("unhandledrejection", (e) => {
  // eslint-disable-next-line no-console
  console.error("🚨 Unhandled Promise Rejection:", e.reason);
  __lastGlobal.reason = e.reason;
});

/* =================== INLINE ERROR BOUNDARY =================== */
class InlineErrorBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null, info: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
    this.setState({ info });
    window.__lastError = { error, info };
  }
  render() {
    const { err, info } = this.state;
    if (err) {
      const name = err?.name || "Error";
      const msg = err?.message || String(err);
      const stack = err?.stack || "";
      const compStack = info?.componentStack || "";
      return (
        <div style={{ padding: 24, fontFamily: "sans-serif" }}>
          <h2>💥 แอปมีข้อผิดพลาด</h2>
          <p><b>{name}</b></p>
          <pre style={{ whiteSpace: "pre-wrap", background: "#f7f7f7", padding: 12 }}>{msg}</pre>
          {stack ? (<><h3>Stack</h3><pre style={{ whiteSpace: "pre-wrap", background: "#f7f7f7", padding: 12 }}>{stack}</pre></>) : null}
          {compStack ? (<><h3>Component Stack</h3><pre style={{ whiteSpace: "pre-wrap", background: "#f7f7f7", padding: 12 }}>{compStack}</pre></>) : null}
          {__lastGlobal.error || __lastGlobal.reason ? (
            <>
              <h3>Last Global Error/Reason</h3>
              <pre style={{ whiteSpace: "pre-wrap", background: "#f7f7f7", padding: 12 }}>
                {String(__lastGlobal.error || __lastGlobal.reason)}
              </pre>
            </>
          ) : null}
          <p>→ เปิด DevTools → Console ดูรายละเอียดซ้ำได้ (window.__lastError)</p>
          <p>→ โหมดปลอดภัย: เพิ่ม <code>#/__safe</code> ท้าย URL เพื่อตัดโค้ดใน &lt;App/&gt;</p>
        </div>
      );
    }
    return this.props.children;
  }
}

/* =================== ROOT PREP =================== */
const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("ไม่พบ <div id='root'>");
// อย่าแตะ innerHTML/children ใด ๆ ก่อน React mount

/* =================== GEO-RISK NAVIGATOR (ONE-TIME) =================== */
(function attachGeoRiskNavigatorOnce() {
  if (window.__geoRiskNavigatorReady) return;
  window.__geoRiskNavigatorReady = true;

  const isHashRouter = /\#\//.test(window.location.href);

  function normalizeUrl(redirectUrl = "") {
    if (!redirectUrl) return null;
    if (/^https?:\/\//i.test(redirectUrl)) return redirectUrl; // external → open new tab
    let path = redirectUrl.startsWith("/") ? redirectUrl : `/${redirectUrl}`;
    if (isHashRouter && !path.startsWith("#/")) return `#${path}`;
    return path;
  }

  function navigate(redirectUrl) {
    const dest = normalizeUrl(redirectUrl);
    if (!dest) return;

    if (/^https?:\/\//i.test(dest)) {
      try { window.open(dest, "_blank", "noopener"); } catch {}
      return;
    }
    if (dest.startsWith("#/")) {
      const next = dest.slice(1); // remove '#'
      if (window.location.hash !== next) window.location.hash = next;
    } else {
      const cur = window.location.pathname + window.location.search + window.location.hash;
      if (cur !== dest) window.location.assign(dest);
    }
  }

  window.__geoRiskNavigate = navigate;

  window.addEventListener(
    "cheetah:georisk-redirect",
    (e) => {
      try {
        const d = e?.detail || {};
        const url = d.url || d.redirectUrl || d.redirect_url || d.redirect_to || "";
        if (url) navigate(url);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[GeoRiskNavigator] event error:", err);
      }
    },
    { passive: true }
  );
})();

/* =================== SAFE LOADER REMOVAL =================== */
function safeRemoveLoader() {
  try {
    const loader = document.getElementById("app-loader");
    if (loader && loader.parentNode && loader.parentNode.contains(loader)) {
      loader.parentNode.removeChild(loader);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Failed to remove loader safely:", e);
  }
}
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") safeRemoveLoader();
});

/* =================== SINGLE MOUNT (idempotent) =================== */
// ใช้ root เดิมถ้ามี (กัน createRoot ซ้ำจากบันเดิลซ้อน)
window.__CHEETAH_ROOT__ = window.__CHEETAH_ROOT__ || createRoot(rootEl);
const root = window.__CHEETAH_ROOT__;
const isSafe = window.location.hash.includes("__safe");

/* =================== RENDER =================== */
const tree = (
  <InlineErrorBoundary>
    <HashRouter>
      <App />
    </HashRouter>
  </InlineErrorBoundary>
);

if (isSafe) {
  root.render(
    <div style={{ padding: 24, fontFamily: "sans-serif" }}>
      ✅ Safe screen — React mount ใช้ได้
    </div>
  );
  queueMicrotask(safeRemoveLoader);
} else {
  if (DEV) {
    root.render(<React.StrictMode>{tree}</React.StrictMode>);
  } else {
    root.render(tree);
  }
  queueMicrotask(safeRemoveLoader);
}
